import { useState } from 'react';
import { useHandlers } from '../../../../context/page-context';
import { useStorefrontUrl } from '../../../../hooks/use-storefront-url';
import { useTrackedCustomer } from '../customer/use-tracked-customer';

export type UseInitiateTestOrderResult = {
  /** Opens the storefront for the buyer to sign in and check out. */
  initiateTestOrder: () => void;
  buyerEmail: string | undefined;
  /** Customer and storefront URL both resolved — the CTA can act. */
  isReady: boolean;
  /** The CTA has fired and the storefront tab was opened. */
  hasInitiated: boolean;
};

/**
 * Initiates the test-order journey: opens the storefront so the buyer can sign in and check out.
 * The order itself is placed by the buyer on the storefront; the cart is prefilled separately
 * (see {@link useCartPrefill}) and completion is detected separately (the order shows up as a
 * purchase) — see {@link useChecklistStatus}.
 */
export function useInitiateTestOrder(): UseInitiateTestOrderResult {
  const { customer } = useTrackedCustomer();
  const { navigationHandler } = useHandlers();
  const storefrontUrl = useStorefrontUrl();
  const [hasInitiated, setHasInitiated] = useState<boolean>(false);

  function initiateTestOrder() {
    if (!storefrontUrl) {
      return;
    }
    // Opened synchronously within the click gesture so the browser doesn't treat it as a pop-up.
    navigationHandler.navigate(storefrontUrl, { absolute: true, newTab: true });
    setHasInitiated(true);
  }

  return {
    initiateTestOrder,
    buyerEmail: customer?.email,
    isReady: !!customer && !!storefrontUrl,
    hasInitiated,
  };
}
